import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  FormControl,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./styles/_index.scss";

mapboxgl.accessToken =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoiZHNkYXNhIiwiYSI6ImNsemp4d2JkMzByeHkycXF5cWhuc2sybGUifQ.Dag-kzM-Lw7VcHNz4XGeSg";

interface OperationsComponentProps {
  open: boolean;
  close: () => void;
  isAdmin: boolean;
  showCancelButton?: boolean;
  ssoMultiClient?: boolean;
  ssoClientIds?: string[];
}

interface OPdata {
  forecast_description: string;
  forecast_id: string;
  display_description?: string;
  client_id?: string;
  client_name?: string;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  lat?: string | number | null;
  lon?: string | number | null;
  days?: number;
}

interface ClientLocation {
  client_id: string;
  client_name: string;
  latitude: number;
  longitude: number;
  operations: OPdata[];
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_IP,
  timeout: 30000,
  headers: { "Content-Type": "application/json; charset=UTF-8" },
});

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("token");
  config.headers.Authorization = token
    ? `Bearer ${token}`
    : "Basic " + btoa("admin:admin");
  return config;
});

function extractDaysFromLabel(label: string): number | null {
  const match = label.trimEnd().match(/(\d+)$/);
  if (!match) {
    return null;
  }
  const trailingNumber = match[1];
  const lastDigit = parseInt(trailingNumber[trailingNumber.length - 1], 10);

  if (lastDigit === 0 && trailingNumber.length >= 2) {
    const lastTwoDigits = parseInt(trailingNumber.slice(-2), 10);
    return lastTwoDigits;
  }
  return lastDigit;
}

function formatOperationLabel(
  description: string,
  days?: number | null,
): string {
  const dashCount = (description.match(/-/g) || []).length;
  if (dashCount >= 3) {
    let dashesFound = 0;
    let sliceIndex = -1;
    for (let i = 0; i < description.length; i++) {
      if (description[i] === "-") {
        dashesFound++;
        if (dashesFound === 3) {
          sliceIndex = i;
          break;
        }
      }
    }
    const baseLabel = description.slice(0, sliceIndex).trimEnd();
    const extractedDays = extractDaysFromLabel(baseLabel);
    if (extractedDays === null) {
      return baseLabel;
    }
    const result = `${baseLabel} - ${extractedDays} Days Ahead`;
    return result;
  }

  if (dashCount === 2) {
    // Take segment after the 2nd dash and trim spaces
    const parts = description.split("-");
    const afterSecondDash = parts.slice(2).join("-").trim();
    // Must be pure digits only and less than 5 digits — longer numbers are codes/IDs
    if (!/^\d+$/.test(afterSecondDash) || afterSecondDash.length >= 5) {
      return description;
    }

    const lastDigit = parseInt(afterSecondDash[afterSecondDash.length - 1], 10);
    const result = `${description} - ${lastDigit} Days Ahead`;
    return result;
  }
  return description;
}

function generateGridGeoJSON(zoom: number): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  let step: number;
  if (zoom <= 1) step = 30;
  else if (zoom <= 2) step = 15;
  else if (zoom <= 3) step = 10;
  else if (zoom <= 4) step = 5;
  else if (zoom <= 5) step = 2;
  else if (zoom <= 6) step = 1;
  else if (zoom <= 7) step = 0.5;
  else if (zoom <= 8) step = 0.25;
  else if (zoom <= 10) step = 0.1;
  else if (zoom <= 12) step = 0.05;
  else if (zoom <= 14) step = 0.025;
  else if (zoom <= 16) step = 0.01;
  else step = 0.005;

  for (let lat = -90; lat <= 90; lat += step) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [[-180, lat], [180, lat]] },
      properties: { gridType: "parallel" },
    });
  }
  for (let lon = -180; lon <= 180; lon += step) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [[lon, -90], [lon, 90]] },
      properties: { gridType: "meridian" },
    });
  }
  return { type: "FeatureCollection", features };
}

export default function OperationsComponent(props: OperationsComponentProps) {
  const [open, setOpen] = useState(props.open);
  const [clients, setClients] = useState<any>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>();
  const [oData, setOData] = useState<OPdata[]>([]);
  const [loading, setLoading] = useState(false);
  const [ssoClientNames, setSsoClientNames] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(props.isAdmin);
  const [ssoMultiClient, setSsoMultiClient] = useState(
    props.ssoMultiClient || false,
  );
  const [ssoClientIds, setSsoClientIds] = useState<string[]>(
    props.ssoClientIds || [],
  );
  const [clientLocations, setClientLocations] = useState<ClientLocation[]>([]);
  const [dropdownLocation, setDropdownLocation] =
    useState<ClientLocation | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hoverCoords, setHoverCoords] = useState<{ lat: number; lng: number } | null>(null);

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const mapInitialized = useRef(false);
  const dataFetchedRef = useRef(false);
  const lastFetchedClientIds = useRef<string>("");
  const zoomTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const navigate = useNavigate();

  const storedData = useMemo(
    () => ({
      clientId: localStorage.getItem("client_id"),
      userName: localStorage.getItem("user_name"),
      userType: localStorage.getItem("type"),
      ssoMultiClient: localStorage.getItem("sso_multi_client") === "true",
      ssoClientIds: localStorage.getItem("sso_client_ids"),
      ssoClientNames: localStorage.getItem("sso_client_names"),
      lat: localStorage.getItem("lat"),
      lon: localStorage.getItem("lon"),
    }),
    [],
  );

  useEffect(() => {
    if (props.open) {
      dataFetchedRef.current = false;
      mapInitialized.current = false;
      lastFetchedClientIds.current = "";
      setClientLocations([]);
      setOData([]);
      setSelectedClientId(undefined);
      setDropdownLocation(null);
      setDropdownPosition(null);
      setHoverCoords(null);
    }

    if (storedData.userType) {
      setIsAdmin(storedData.userType === "admin");
    }

    if (
      !props.ssoMultiClient &&
      storedData.ssoMultiClient &&
      storedData.ssoClientIds
    ) {
      try {
        const parsedIds = JSON.parse(storedData.ssoClientIds);
        setSsoMultiClient(true);
        setSsoClientIds(parsedIds);
      } catch (error) {
        console.error("[Operations] Failed to parse SSO client IDs:", error);
      }
    } else if (props.ssoMultiClient) {
      setSsoMultiClient(props.ssoMultiClient);
      setSsoClientIds(props.ssoClientIds || []);
    }
  }, [props.open]);

  const applyMainExtendedLogic = useCallback((data: OPdata[]): OPdata[] => {
    const pattern = /^(\w+)-(\w+)\s+(\d+)-(\d+)/;
    const grouped: { [key: string]: OPdata[] } = {};
    const noTransform: OPdata[] = [];

    data.forEach((item) => {
      const match = item.forecast_description.match(pattern);
      if (match) {
        const [, location1, location2, num1, num2] = match;
        if (location1 === location2 && parseInt(num2[num2.length - 1]) < 9) {
          const prefix = `${location1}-${location2} ${num1}-`;
          if (!grouped[prefix]) grouped[prefix] = [];
          grouped[prefix].push({ ...item });
          return;
        }
      }
      noTransform.push({
        ...item,
        display_description: item.forecast_description,
      });
    });

    const result: OPdata[] = [];
    Object.entries(grouped).forEach(([prefix, items]) => {
      if (items.length >= 2) {
        items.sort((a, b) => parseInt(a.forecast_id) - parseInt(b.forecast_id));
        items.forEach((item, idx) => {
          const match = item.forecast_description.match(pattern);
          if (match) {
            const [, location1, location2, num1, num2] = match;
            item.display_description = `${location1}-${location2} ${num1}-${num2}-${idx === 0 ? "Main" : "Extended"}`;
          }
          result.push(item);
        });
      } else {
        items.forEach((item) => {
          item.display_description = item.forecast_description;
          result.push(item);
        });
      }
    });

    result.push(...noTransform);
    return result;
  }, []);

  const groupOperationsByLocation = useCallback(
    async (
      operations: OPdata[],
      isSSOMulti: boolean,
    ): Promise<ClientLocation[]> => {
      const locationsMap: { [key: string]: ClientLocation } = {};
      const { lat: storedLat, lon: storedLon } = storedData;

      for (const op of operations) {
        let latitude: number | null = null;
        let longitude: number | null = null;
        const clientId = op.client_id || "default";

        if (op.lat && op.lon) {
          latitude = parseFloat(String(op.lat));
          longitude = parseFloat(String(op.lon));
        } else if (!isSSOMulti && storedLat && storedLon) {
          latitude = parseFloat(storedLat);
          longitude = parseFloat(storedLon);
        }

        if (latitude && longitude) {
          const locationKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
          if (locationsMap[locationKey]) {
            locationsMap[locationKey].operations.push(op);
          } else {
            locationsMap[locationKey] = {
              client_id: clientId,
              client_name: op.client_name || `Client ${clientId}`,
              latitude,
              longitude,
              operations: [op],
            };
          }
        }
      }

      return Object.values(locationsMap);
    },
    [storedData],
  );

  const handleOperationSelect = useCallback(
    async (operation: OPdata, location: ClientLocation) => {
      const clientId = location.client_id;
      const forecastId = operation.forecast_id;
      const forecastDescription = operation.forecast_description;

      localStorage.setItem("fid", forecastId);
      localStorage.setItem("client_id", clientId);
      localStorage.setItem("operation", forecastDescription);
      localStorage.setItem("project", forecastDescription);
      localStorage.setItem("lat", String(location.latitude));
      localStorage.setItem("lon", String(location.longitude));

      let userName = storedData.userName;
      if (ssoMultiClient && ssoClientIds?.length > 0) {
        const clientIndex = ssoClientIds.findIndex((id) => id === clientId);
        if (clientIndex !== -1 && ssoClientNames[clientIndex]) {
          userName = `SSO-${ssoClientNames[clientIndex]}`;
          localStorage.setItem("user_name", userName);
        }
      }

      try {
        await apiClient.post("/api/setoperation/", {
          name: userName,
          fd: forecastDescription,
        });

        sessionStorage.setItem("auth_operation", "true");
        setDropdownLocation(null);
        setDropdownPosition(null);
        props.close();

        setTimeout(() => {
          navigate("/dashboard");
          window.location.reload();
        }, 100);
      } catch (error) {
        alert(
          `Failed to set operation: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    [
      ssoMultiClient,
      ssoClientIds,
      ssoClientNames,
      storedData.userName,
      navigate,
      props,
    ],
  );

  const handleMarkerClick = useCallback(
    (location: ClientLocation, markerElement: HTMLElement) => {
      if (location.operations.length === 1) {
        handleOperationSelect(location.operations[0], location);
      } else {
        const rect = markerElement.getBoundingClientRect();
        setDropdownPosition({ x: rect.left + rect.width / 2, y: rect.top });
        setDropdownLocation(location);
      }
    },
    [handleOperationSelect],
  );

  const initializeMap = useCallback(
    (locations: ClientLocation[]) => {
      if (!mapContainer.current || mapInitialized.current) return;

      if (map.current) {
        map.current.remove();
        map.current = null;
      }

      try {
        map.current = new mapboxgl.Map({
  container: mapContainer.current,
  style: "mapbox://styles/mapbox/light-v10",   
  center: [-98.5795, 39.8283],
  zoom: 4,
  projection: "mercator" as any,
});

        map.current.addControl(new mapboxgl.NavigationControl(), "top-left");

        map.current.on("load", () => {
          markers.current.forEach((marker) => marker.remove());
          markers.current = [];

          locations.forEach((location) => {
            const hasMultipleOps = location.operations.length > 1;

            const marker = new mapboxgl.Marker({
              color: hasMultipleOps ? "#FF6B00" : "#0077FF",
              draggable: false,
            })
              .setLngLat([location.longitude, location.latitude])
              .addTo(map.current!);

            const markerElement = marker.getElement();
            const markerSvg = markerElement.querySelector("svg");
            if (markerSvg) markerSvg.style.transition = "transform 0.2s ease";

            const label = document.createElement("div");
            label.className = "marker-label-fixed";
            label.style.cssText = `
              position: absolute;
              bottom: 100%;
              left: 50%;
              transform: translateX(-50%);
              margin-bottom: 8px;
              padding: 6px 12px;
              background: white;
              border: 2px solid ${hasMultipleOps ? "#FF6B00" : "#0077FF"};
              border-radius: 6px;
              font-size: 12px;
              font-weight: 600;
              color: ${hasMultipleOps ? "#FF6B00" : "#0077FF"};
              white-space: nowrap;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              pointer-events: none;
              z-index: 1;
              transition: all 0.2s ease;
            `;

            if (hasMultipleOps) {
              label.textContent = `${location.operations.length} Forecasts`;
            } else {
              const op = location.operations[0];
              const rawDesc = op.display_description || op.forecast_description;
              label.textContent = formatOperationLabel(rawDesc, op.days);
            }

            markerElement.appendChild(label);

            markerElement.addEventListener("mouseenter", () => {
              if (markerSvg) markerSvg.style.transform = "scale(1.2)";
              label.style.background = hasMultipleOps ? "#FF6B00" : "#0077FF";
              label.style.color = "white";
            });

            markerElement.addEventListener("mouseleave", () => {
              if (markerSvg) markerSvg.style.transform = "scale(1)";
              label.style.background = "white";
              label.style.color = hasMultipleOps ? "#FF6B00" : "#0077FF";
            });

            markerElement.addEventListener("click", (e) => {
              e.stopPropagation();
              handleMarkerClick(location, markerElement);
            });

            markers.current.push(marker);
          });

          if (locations.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            locations.forEach((loc) =>
              bounds.extend([loc.longitude, loc.latitude]),
            );
            map.current?.fitBounds(bounds, {
              padding: { top: 100, bottom: 100, left: 100, right: 100 },
              maxZoom: 8,
              duration: 1000,
            });
          }

          // Grid setup
          map.current!.addSource("grid-source", {
            type: "geojson",
            data: generateGridGeoJSON(4),
          });
         map.current!.addLayer({
  id: "grid-layer",
  type: "line",
  source: "grid-source",
  paint: {
    "line-color": "#000000",   
    "line-width": 1,           
    "line-opacity": 0.5,     
  },
  layout: { "line-cap": "round", "line-join": "round" },
});
map.current!.setPaintProperty("grid-layer", "line-dasharray", [2, 2]);

          // Update grid on zoom
          map.current!.on("zoomend", () => {
            clearTimeout(zoomTimeoutRef.current);
            zoomTimeoutRef.current = setTimeout(() => {
              if (!map.current) return;
              const src = map.current.getSource("grid-source") as mapboxgl.GeoJSONSource;
              if (src) src.setData(generateGridGeoJSON(map.current.getZoom()));
            }, 200);
          });

          // Hover coords
          map.current!.on("mousemove", (e: mapboxgl.MapMouseEvent) => {
            setHoverCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
          });
          map.current!.on("mouseout", () => {
            setHoverCoords(null);
          });

          mapInitialized.current = true;
        });

        map.current.on("click", () => {
          setDropdownLocation(null);
          setDropdownPosition(null);
        });
      } catch (error) {
        console.error("[Operations] Error creating map:", error);
      }
    },
    [handleMarkerClick],
  );

  const fetchLatLonBatch = useCallback(
    async (operations: OPdata[]): Promise<OPdata[]> => {
      const BATCH_SIZE = 10;
      const results: OPdata[] = [];

      for (let i = 0; i < operations.length; i += BATCH_SIZE) {
        const batch = operations.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (op) => {
            try {
              const response = (await Promise.race([
                apiClient.get(`/api/getlatlon/${op.forecast_id}/`),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error("timeout")), 8000),
                ),
              ])) as any;
              return { ...op, lat: response.data.lat, lon: response.data.lon };
            } catch (e) {
              return { ...op, lat: null, lon: null };
            }
          }),
        );
        results.push(...batchResults);
      }

      return results;
    },
    [],
  );

  const fetchForecastDaysBatch = useCallback(
    async (operations: OPdata[]): Promise<OPdata[]> => {
      try {
        const forecastIds = operations.map((op) => op.forecast_id);
        if (forecastIds.length === 0) return operations;

        const response = await apiClient.post("/api/forecast-days-batch/", {
          forecast_ids: forecastIds,
        });
        const daysMap = response.data;

        return operations.map((op) => ({
          ...op,
          days: daysMap[op.forecast_id] || 0,
        }));
      } catch (error) {
        console.error("[Operations] Error fetching forecast days:", error);
        return operations.map((op) => ({ ...op, days: 0 }));
      }
    },
    [],
  );

  const fetchData = useCallback(
    async (clientId: string) => {
      if (!clientId) return;

      setLoading(true);
      try {
        const response = await apiClient.get(`/api/operations12/${clientId}/`);

        if (!response.data || !Array.isArray(response.data)) {
          alert("Received invalid data from server. Please try again.");
          return;
        }

        const dataWithLatLon = await fetchLatLonBatch(
          response.data.map((op: OPdata) => ({
            ...op,
            client_id: clientId,
            client_name:
              clients.find((c: any) => c.client_id.toString() === clientId)
                ?.client_name || `Client ${clientId}`,
          })),
        );

        const dataWithDays = await fetchForecastDaysBatch(dataWithLatLon);
        const transformedData = applyMainExtendedLogic(dataWithDays);
        setOData(transformedData);

        const locations = await groupOperationsByLocation(
          transformedData,
          false,
        );
        setClientLocations(locations);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 500) {
          alert(
            "Server error. Please try selecting the client again or contact support.",
          );
        } else if (axios.isAxiosError(error)) {
          alert(`Failed to load operations: ${error.message}`);
        }
        setOData([]);
        setClientLocations([]);
      } finally {
        setLoading(false);
      }
    },
    [
      clients,
      fetchLatLonBatch,
      fetchForecastDaysBatch,
      applyMainExtendedLogic,
      groupOperationsByLocation,
    ],
  );

  const fetchDataWithRetry = useCallback(
    async (clientId: string, retries = 2) => {
      for (let i = 0; i <= retries; i++) {
        try {
          await fetchData(clientId);
          return;
        } catch (error) {
          if (i === retries) {
            if (axios.isAxiosError(error)) {
              if (error.response?.status === 500) {
                alert(
                  "Server error occurred after multiple attempts. Please contact support.",
                );
              } else {
                alert(`Failed to load operations: ${error.message}`);
              }
            }
            setOData([]);
            setClientLocations([]);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    },
    [fetchData],
  );

  const fetchDataForAllSSOClients = useCallback(
    async (clientIds: string[]) => {
      if (!clientIds || clientIds.length === 0) return;

      setLoading(true);
      try {
        const storedNames = (() => {
          try {
            return JSON.parse(localStorage.getItem("sso_client_names") || "[]");
          } catch {
            return [];
          }
        })();

        const ssoPrefixes: string[] = (() => {
          try {
            const stored = localStorage.getItem("sso_prefixes");
            return stored ? JSON.parse(stored) : [];
          } catch {
            return [];
          }
        })();

        const clientInfoMap: { [key: string]: any } = {};
        clientIds.forEach((id, index) => {
          clientInfoMap[id] = { name: storedNames[index] || `Client ${id}` };
        });

        const ssoClients = clientIds.map((id, index) => ({
          client_id: id,
          client_name: storedNames[index] || `Client ${id}`,
        }));

        setClients(ssoClients);
        setSsoClientNames(storedNames);

        const prefixParam =
          ssoPrefixes.length > 0 ? ssoPrefixes.join(",") : null;

        const operationsResponses = await Promise.all(
          clientIds.map((clientId) =>
            apiClient
              .get(`/api/operations12/${clientId}/`, {
                params: prefixParam ? { prefix: prefixParam } : {},
              })
              .catch((err) => {
                return { data: [] };
              }),
          ),
        );

        let allOperations: OPdata[] = [];
        operationsResponses.forEach((response, index) => {
          const clientId = clientIds[index];
          const ops = response.data.map((op: OPdata) => ({
            ...op,
            client_id: clientId,
            client_name: clientInfoMap[clientId]?.name || `Client ${clientId}`,
          }));
          allOperations.push(...ops);
        });

        if (ssoPrefixes.length > 0) {
          allOperations = allOperations.filter((op) => {
            const desc = op.forecast_description?.toUpperCase() || "";
            return ssoPrefixes.some((prefix: string) =>
              desc.startsWith(prefix),
            );
          });
        }

        const opsWithLatLon = await fetchLatLonBatch(allOperations);
        const opsWithDays = await fetchForecastDaysBatch(opsWithLatLon);
        const transformedData = applyMainExtendedLogic(opsWithDays);
        setOData(transformedData);

        const locations = await groupOperationsByLocation(
          transformedData,
          true,
        );
        setClientLocations(locations);
        dataFetchedRef.current = true;
      } catch (error) {
        console.error("[SSO] Error in SSO multi-client fetch:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchLatLonBatch,
      fetchForecastDaysBatch,
      applyMainExtendedLogic,
      groupOperationsByLocation,
    ],
  );

  useEffect(() => {
    // Wait until props.open is true and SSO state is fully resolved
    if (!props.open) return;
    if (!isAdmin) return;
    if (ssoMultiClient) return;

    // Extra guard: double-check localStorage directly before calling
    const rawSsoMultiClient =
      localStorage.getItem("sso_multi_client") === "true";
    const rawSsoClientIds = localStorage.getItem("sso_client_ids");
    if (rawSsoMultiClient && rawSsoClientIds) {
      return;
    }
    apiClient
      .get("/api/getclients/")
      .then((response) => setClients(response.data))
      .catch((error) =>
        console.error("[Operations] Error fetching clients:", error),
      );
  }, [isAdmin, ssoMultiClient, props.open]);

  useEffect(() => {
    if (!props.open) return;

    if (isAdmin && !ssoMultiClient) {
      if (selectedClientId) {
        if (map.current) {
          markers.current.forEach((marker) => marker.remove());
          markers.current = [];
          map.current.remove();
          map.current = null;
          mapInitialized.current = false;
        }
        setClientLocations([]);
        setOData([]);
        fetchDataWithRetry(String(selectedClientId));
      }
    } else if (!isAdmin && !ssoMultiClient) {
      if (dataFetchedRef.current) return;
      const storedClientId = localStorage.getItem("client_id");
      if (storedClientId) {
        dataFetchedRef.current = true;
        fetchDataWithRetry(storedClientId);
      } else {
        setLoading(false);
      }
    }
  }, [
    selectedClientId,
    isAdmin,
    ssoMultiClient,
    props.open,
    fetchDataWithRetry,
  ]);

  useEffect(() => {
    if (
      !props.open ||
      !ssoMultiClient ||
      !ssoClientIds ||
      ssoClientIds.length === 0
    )
      return;

    const clientIdsKey = [...ssoClientIds].sort().join(",");
    if (lastFetchedClientIds.current === clientIdsKey && dataFetchedRef.current)
      return;

    lastFetchedClientIds.current = clientIdsKey;
    fetchDataForAllSSOClients(ssoClientIds);
  }, [ssoMultiClient, ssoClientIds, props.open, fetchDataForAllSSOClients]);

  useEffect(() => {
    if (
      props.open &&
      clientLocations.length > 0 &&
      mapContainer.current &&
      !mapInitialized.current
    ) {
      const timer = setTimeout(() => initializeMap(clientLocations), 50);
      return () => clearTimeout(timer);
    }
  }, [clientLocations.length, props.open, initializeMap]);

  useEffect(() => {
    return () => {
      clearTimeout(zoomTimeoutRef.current);
      if (map.current) {
        markers.current.forEach((marker) => marker.remove());
        markers.current = [];
        map.current.remove();
        map.current = null;
        mapInitialized.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (!props.open && map.current) {
      clearTimeout(zoomTimeoutRef.current);
      markers.current.forEach((marker) => marker.remove());
      markers.current = [];
      map.current.remove();
      map.current = null;
      mapInitialized.current = false;
    }
  }, [props.open]);

  const handleCancel = useCallback(() => props.close(), [props]);

  return props.open ? (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          if (!(props.isAdmin ? selectedClientId : true)) return;
          setDropdownLocation(null);
          setDropdownPosition(null);
          props.close();
        }
      }}
      disableEscapeKeyDown
      maxWidth={false}
      fullWidth
      fullScreen
      PaperProps={{
        style: {
          margin: 0,
          maxWidth: "100%",
          maxHeight: "100%",
          borderRadius: 0,
          width: "100vw",
          height: "100vh",
        },
      }}
    >
      <DialogContent
        style={{ padding: 0, overflow: "hidden", height: "100vh" }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {isAdmin && !ssoMultiClient && !selectedClientId && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                backgroundColor: "white",
                padding: "40px",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                minWidth: "400px",
              }}
            >
              <h2
                style={{
                  marginBottom: "20px",
                  textAlign: "center",
                  color: "#333",
                }}
              >
                Select Client
              </h2>
              {clients.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={24} />
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    Loading clients...
                  </p>
                </Box>
              ) : (
                <FormControl fullWidth>
                  <Select
                    value={selectedClientId || ""}
                    onChange={(e) =>
                      setSelectedClientId(e.target.value as string)
                    }
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Choose a client
                    </MenuItem>
                    {clients.map((option: any) => (
                      <MenuItem
                        key={option.client_id}
                        value={option.client_id.toString()}
                      >
                        {option.client_name?.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                backgroundColor: "#f5f5f5",
              }}
            >
              <CircularProgress size={50} />
              <p style={{ marginTop: "20px", color: "#666", fontSize: "16px" }}>
                {ssoMultiClient
                  ? "Loading SSO locations..."
                  : "Loading locations..."}
              </p>
            </Box>
          ) : (
            <>
              {(selectedClientId || ssoMultiClient || !isAdmin) &&
                clientLocations.length > 0 && (
                  <div
                    ref={mapContainer}
                    style={{ width: "100%", height: "100%" }}
                  />
                )}
              {!loading &&
                (selectedClientId || !isAdmin) &&
                clientLocations.length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      flexDirection: "column",
                    }}
                  >
                    <p style={{ color: "#999", fontSize: "18px" }}>
                      No locations found
                    </p>
                  </Box>
                )}
            </>
          )}

          {dropdownLocation && dropdownPosition && (
            <Paper
              sx={{
                position: "fixed",
                left: dropdownPosition.x,
                top: dropdownPosition.y,
                transform: "translate(-50%, -100%)",
                marginTop: "-20px",
                zIndex: 2000,
                maxHeight: "300px",
                width: "280px",
                overflow: "auto",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: "2px solid #FF6B00",
                  backgroundColor: "#fff",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="#FF6B00"
                >
                  Select Project ({dropdownLocation.operations.length})
                </Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {dropdownLocation.operations.map((op, index) => {
                  const description =
                    op.display_description || op.forecast_description;
                  const daysText = op.days ? ` - ${op.days} Days Ahead` : "";
                  return (
                    <ListItem key={op.forecast_id} disablePadding>
                      <ListItemButton
                        onClick={() =>
                          handleOperationSelect(op, dropdownLocation)
                        }
                        sx={{
                          "&:hover": { backgroundColor: "#FFF3E0" },
                          borderBottom:
                            index < dropdownLocation.operations.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                      >
                        <ListItemText
                          primary={`${description}${daysText}`}
                          secondary={op.client_name?.toUpperCase()}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#333",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "12px",
                            color: "#666",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}

          {hoverCoords && (
            <div className="map-coordinates-display">
              Lat: {hoverCoords.lat.toFixed(5)}, Lon: {hoverCoords.lng.toFixed(5)}
            </div>
          )}

          {props.showCancelButton && (
            <Button
              onClick={handleCancel}
              variant="outlined"
              color="error"
              sx={{
                position: "absolute",
                bottom: "20px",
                left: "20px",
                zIndex: 1000,
                color: "red",
                borderColor: "red",
                backgroundColor: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 0, 0, 0.1)",
                  borderColor: "red",
                },
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  ) : null;
}